/**
 * Unified Device Profile API — backend calls for the V2 device profile page.
 *
 * Supports both Inovonics (manufacture_id=1) and Palcare (manufacture_id=4) devices.
 *
 * SHARED READ (apiFetch):
 *   sp_dev_getareasbycampusid      (area dropdown)
 *   sp_dev_getresidentsbycampusid  (resident dropdown)
 *   getcareflowwindows             (wellness / careflow)
 *   getdevicelogs                  (device log entries)
 *   core_preform_event_notes       (preformatted note options)
 *
 * INOVONICS-ONLY READ (apiFetch):
 *   getinovonicsdeviceprofile      (Inovonics device basic info)
 *   att_inovonics_use_entity       (device behavior options)
 *
 * PALCARE-ONLY READ (apiFetch):
 *   getpalcaredeviceprofile        (Palcare device basic info)
 *   getppendantdspprofilelist      (DSP line profile options)
 *   getpalcarelastlocation         (GPS location polling)
 *
 * SHARED READ (apiReport):
 *   dashboardhourchart             (hourly alarm chart)
 *   dashboardweekchart             (weekly trending chart)
 *   eventReasonSummary             (event reason breakdown)
 *   getlimitedevents               (recent events)
 *
 * INOVONICS-ONLY READ (apiQueryItems):
 *   DebugSerialNumber              (DynamoDB debug data)
 *
 * SHARED WRITE (apiTransaction):
 *   device_edit_basic              (edit device info)
 *   device_unassign                (unassign/deactivate)
 *   temp_mute_device               (suppress / resume alarms)
 *
 * SHARED WRITE (apiSave):
 *   core_event_notes PUT           (add event notes)
 *
 * INOVONICS-ONLY UTILITY (apiMqtt):
 *   deviceLogging                  (start debug logging)
 *
 * PALCARE-ONLY UTILITY (apiRemoteCommand):
 *   location                       (find device GPS)
 *   panic                          (alarm device remotely)
 */

import { apiFetch, apiTransaction, apiReport, apiQueryItems, apiMqtt, apiRemoteCommand } from '../../../../services/api.js';
import { getCampusConfig } from '../../../../services/session.js';
import { markEventsChanged } from '../../../../services/reportCache.js';

// ---------------------------------------------------------------------------
// READ — Basic device data (600s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch Inovonics device profile by campus + device ID. */
export function fetchInovonicsProfile(campusId, deviceId) {
  return apiFetch({
    procedure: 'getinovonicsdeviceprofile',
    reqType: 'stored',
    payload: { device_filter: deviceId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch Palcare device profile by campus + device ID. */
export function fetchPalcareProfile(campusId, deviceId) {
  return apiFetch({
    procedure: 'getpalcaredeviceprofile',
    reqType: 'stored',
    payload: { device_filter: deviceId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch all areas for the campus (dropdown options). */
export function fetchAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch all residents for the campus (dropdown options). */
export function fetchResidents(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getresidentsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch careflow windows for the campus (wellness tab). */
export function fetchCareflowWindows(campusId) {
  return apiFetch({
    procedure: 'getcareflowwindows',
    reqType: 'stored',
    payload: {
      campus_id: campusId,
      care_types: '1,3,4,5',
      care_event_types: '1',
    },
    readOnly: true,
  });
}

/** Fetch device log entries. */
export function fetchDeviceLogs(deviceId) {
  return apiFetch({
    procedure: 'getdevicelogs',
    reqType: 'stored',
    pKey: 'device_id',
    pVal: deviceId,
    readOnly: true,
  });
}

/** Fetch device behavior options by device type. */
export function fetchDeviceUseMap(deviceTypeId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'att_inovonics_use_entity',
    pKey: 'inovonics_type_id',
    pVal: deviceTypeId,
    readOnly: true,
  });
}

/** Fetch preformatted event note options. */
export function fetchPreformNotes(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_preform_event_notes',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Reporting data (1200s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch hourly alarm chart data. */
export function fetchHourChart(campusId, deviceId) {
  return apiReport({
    repType: 'dashboardhourchart',
    campusId,
    deviceFilter: deviceId.toString(),
  });
}

/** Fetch weekly trending chart data. */
export function fetchWeekChart(campusId, deviceId) {
  return apiReport({
    repType: 'dashboardweekchart',
    campusId,
    deviceFilter: deviceId.toString(),
  });
}

/** Fetch event reason summary for a date range. */
export function fetchReasonSummary(campusId, deviceId, startDate, endDate) {
  return apiReport({
    repType: 'eventReasonSummary',
    campusId,
    deviceFilter: deviceId.toString(),
    startDate,
    endDate,
    // See areaProfileApi.fetchReasonSummary -- picks the cloud vs non-cloud
    // reason proc for THIS report's campus.
    isCloud: Boolean(getCampusConfig(campusId)?.is_cloud),
  });
}

/** Fetch limited recent events for a device. */
export function fetchLimitedEvents(campusId, deviceId) {
  return apiFetch({
    procedure: 'getlimitedevents',
    reqType: 'stored',
    payload: { campus_id: campusId, device_id: deviceId.toString() },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Debug data (DynamoDB)
// ---------------------------------------------------------------------------

/** Fetch debug serial data from DynamoDB. */
export function fetchDebugData(campusId, serialNum) {
  return apiQueryItems({
    table: 'DebugSerialNumber',
    partitionKey: 'SerialNum',
    partitionValue: serialNum.toString(),
    campusId,
  });
}

// ---------------------------------------------------------------------------
// WRITE — Device editing
// ---------------------------------------------------------------------------

/**
 * Edit device basic info (description, assignment, behavior, serial).
 *
 * V1 TransType: device_edit_basic
 *
 * @param {Array}  payload    — [{ device_id, descriptions, is_active, area_id, resident_id, device_use_id, manufacture_uuid, modified_by, vendor_ids }]
 * @param {Array}  oldPayload — [{ device_id, descriptions, is_active, area_id, resident_id, manufacture_uuid }]
 * @param {object} session    — { campusId, userId, clientId }
 */
export function editDeviceBasicInfo(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'device_edit_basic',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign and deactivate a device.
 *
 * V1 TransType: device_unassign
 *
 * @param {Array}  payload    — [{ device_id, is_active: false, resident_id: null, area_id: null, modified_by, vendor_ids }]
 * @param {Array}  oldPayload — [{ device_id, area_id, resident_id }]
 * @param {object} session    — { campusId, userId, clientId }
 */
export function unassignDevice(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'device_unassign',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Temporarily suppress a device's alarms, or resume them.
 *
 * V1 TransType: temp_mute_device
 *
 * `away_until` is a UTC 'YYYY-MM-DD HH:MM:SS' timestamp for when suppression
 * ends, built by muteUntilTimestamp() in src/utils/deviceMute.js. Pass an empty
 * string to resume alarms immediately — that is the unmute call, not a separate
 * transType, because both write the same column.
 *
 * `campus_id` is intentionally absent from the row: it rides the envelope, same
 * as the other two device transactions in this module.
 *
 * `local_away_until` is the same instant as `away_until` rendered in campus-local
 * time as 'MM/DD/YYYY H:MMAM' — for the device log text, so the logged wording
 * matches what the portal shows the user rather than a UTC string they must
 * mentally convert. Both are empty strings on a resume.
 *
 * @param {Array}  payload    — [{ device_id, away_until, local_away_until, modified_by, vendor_ids }]
 * @param {Array}  oldPayload — [{ device_id, away_until }] previous value, for the audit diff
 * @param {object} session    — { campusId, userId, clientId }
 */
export function tempMuteDevice(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'temp_mute_device',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add a note to an event.
 *
 * V1: mysave → core_event_notes PUT
 *
 * @param {Array}  noteItems — [{ event_id, campus_id, note, modified_by }]
 * @param {object} session   — { campusId, userId, clientId }
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. */
export function addEventNote(noteItems, session) {
    /* Tell any cached report that contains these events that it is stale, so
       coming back to it shows this edit rather than the rows from before it.
       Marked on attempt, not on success: a false-positive stale costs
       one refresh of identical data, a missed one shows the user a
       report that contradicts the edit they just made.
       See docs/REPORT_STATE_RESTORE_PLAN.md O1. */
    markEventsChanged((noteItems || []).map((n) => n.event_id));
  return apiTransaction({
    transType: 'event_note_add',
    payload: noteItems,
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
  });
}

// ---------------------------------------------------------------------------
// UTILITY — MQTT device logging
// ---------------------------------------------------------------------------

/**
 * Start debug logging for a device via MQTT.
 *
 * @param {number} campusId
 * @param {string} clientId  — Session ID
 * @param {Array}  payload   — [{ manufacture_uuid, duration }]
 */
export function startDeviceLogging(campusId, clientId, payload) {
  return apiMqtt({
    operation: 'deviceLogging',
    campusId,
    clientId,
    content: payload,
  });
}

// ---------------------------------------------------------------------------
// PALCARE-ONLY — DSP profiles, location, remote commands
// ---------------------------------------------------------------------------

/** Fetch pendant DSP (line) profile options for a campus. */
export function fetchDspProfiles(campusId) {
  return apiFetch({
    procedure: 'getppendantdspprofilelist',
    reqType: 'stored',
    payload: { campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch last known device location (polled every 5s after find/alarm command). */
export function fetchDeviceLocation(campusId, mac, lastDate) {
  return apiFetch({
    procedure: 'getpalcarelastlocation',
    reqType: 'stored',
    payload: { campus_id: campusId, mac, last_date: lastDate },
    readOnly: false,
  });
}

/**
 * Send a remote command to a Palcare device.
 *
 * @param {number} campusId
 * @param {string} userId
 * @param {string} clientId  — Session ID
 * @param {string} devid     — Device MAC address (manufacture_uuid)
 * @param {string} command   — "location" or "panic"
 * @returns {Promise<boolean>}
 */
export function sendRemoteCommand(campusId, userId, clientId, devid, command) {
  return apiRemoteCommand({
    campusId,
    userId,
    clientId,
    payload: { devid, command },
  });
}
